import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import {
  formatRfcTimeStringToDateAndTimeView,
  getDateTimeFromLocalDateTime,
} from '../../../../utils/dateAndTime/dateAndTime';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { CalendarState } from '../../controller';
import { MemoizedViewModalFactory } from '../viewModel';
import { CalendarErrors, Preference } from '../../../../types/types';
import { getFurthestValidUntilPlan } from '../../../../utils/pricingPlans/pricingPlans';
import { getPaymentDescription } from '../../../../utils/payment/payment';
import {
  isCustomerCannotChooseStaff,
  isServiceOfferedOnline,
  isStaffVariant,
  getFormattedCurrency,
  getServiceCurrency,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { Booking } from '@wix/bookings-checkout-api';
import {
  createBookingDetailsPreferences,
  BookingDetailsPreferences,
} from './bookingDetailsPreferences';
import { DO_NOT_CARE_STAFF } from '../../../../constants/constants';
import { getBookingPreferencesForSelectedTime } from '../../../../utils/bookingPreferences/bookingPreferencesForSelectedTime';
import { BookingPreference } from '../../../../utils/bookingPreferences/bookingPreferences';
import { isCalendarWidget } from '../../../../utils/presets';

export { createDummyBookingDetailsViewModel } from './dummyBookingDetailsViewModel';

export type TimeSlotDetails = {
  locationLabel?: string;
  staffMemberLabel?: string;
  durationLabel?: string;
  addOnsLabel?: string;
};

export type BookingDetailsViewModel = {
  serviceName: string;
  dateAndTime?: string;
  paymentDescription?: string;
  videoConferenceBadgeText?: string;
  preferences?: BookingDetailsPreferences;
  alert?: string;
  addOnsCount?: string;
  openPreferencesModalButton?: {
    text: string;
    ariaLabel: string;
  };
  timeSlotDetails: TimeSlotDetails;
};

export const memoizedBookingDetailsViewModel: MemoizedViewModalFactory<BookingDetailsViewModel> =
  {
    dependencies: {
      state: [
        'servicesInView',
        'serviceVariantsMap',
        'selectableSlotsAtSelectedTime',
        'selectedTime',
        'selectedBookingPreferences',
        'calendarErrors',
        'purchasedPricingPlans',
        'selectedVariantsOptions',
      ],
      settings: [
        'videoConferenceBadge',
        'bookingDetailsPricingPlanText',
        'waitlistIndication',
        'bookingDetailsClearText',
        'preferencesTitle',
        'nextButton',
        'locationLabel',
        'staffMemberLabel',
        'durationLabel',
      ],
      subDependencies: [],
    },
    createViewModel: createBookingDetailsViewModel,
  };

export const getVideoConferencingBadge = (context: CalendarContext) => {
  const { getContent, settingsParams } = context;
  return getContent({
    settingsParam: settingsParams.videoConferenceBadge,
    translationKey: 'app.settings.defaults.video-conference-badge-text',
  });
};

export function createBookingDetailsViewModel({
  state,
  context,
}: ViewModelFactoryParams<
  CalendarState,
  CalendarContext
>): BookingDetailsViewModel {
  const { languageSettings, t, preset } = context;

  const dateRegionalSettingsLocale =
    languageSettings!.dateRegionalSettingsLocale!;

  const {
    servicesInView,
    selectedTime,
    calendarErrors,
    purchasedPricingPlans,
    selectedVariantsOptions,
    rescheduleBookingDetails,
    selectableSlotsAtSelectedTime = [],
    selectedBookingPreferences,
    isPreferencesModalPartOfTheFlow,
  } = state;
  const service = servicesInView[0];

  const serviceName = service.name!;

  let alert;
  if (calendarErrors.includes(CalendarErrors.NO_VALID_PRICING_PLAN_WARNING)) {
    const furthestValidUntilPlan = getFurthestValidUntilPlan(
      purchasedPricingPlans,
    );
    alert = furthestValidUntilPlan
      ? t('app.booking-details.pricing-plan-alert', {
          planName: furthestValidUntilPlan.planName,
          planDate: getDateTimeFromLocalDateTime(
            furthestValidUntilPlan.validUntil!,
          ),
        })
      : undefined;
  }

  const videoConferenceBadgeText = isServiceOfferedOnline(service)
    ? getVideoConferencingBadge(context)
    : '';

  const paymentDescription = !rescheduleBookingDetails
    ? getPaymentDescription({
        service,
        selectedVariantsOptions,
        context,
      })
    : undefined;

  const dateAndTime = selectedTime
    ? formatRfcTimeStringToDateAndTimeView(
        selectedTime,
        dateRegionalSettingsLocale,
      )
    : '';

  const baseBookingPreferences = getBookingPreferencesForSelectedTime({
    selectableSlotsAtSelectedTime,
    selectedBookingPreferences,
    calendarErrors,
    context,
    state,
  });

  const preferences = createBookingDetailsPreferences({
    state,
    context,
    preferences: baseBookingPreferences,
  });

  return {
    serviceName,
    paymentDescription,
    dateAndTime,
    videoConferenceBadgeText,
    preferences,
    alert,
    addOnsCount: getAddonsCount({ context, rescheduleBookingDetails }),
    openPreferencesModalButton: isPreferencesModalPartOfTheFlow
      ? {
          text: t('app.summary.open-preferences-modal-button.text'),
          ariaLabel: t('app.summary.open-preferences-modal-button.aria-label'),
        }
      : undefined,
    timeSlotDetails: getTimeSlotDetails({
      context,
      state,
      baseBookingPreferences,
    }),
  };
}

const getAddonsCount = ({
  context,
  rescheduleBookingDetails,
}: {
  context: CalendarContext;
  rescheduleBookingDetails?: Booking;
}) => {
  const { t } = context;

  const count = rescheduleBookingDetails?.bookedAddOns?.length;
  return count ? t('app.summary.addons-count', { count }) : undefined;
};

const getTimeSlotDetails = ({
  context,
  state,
  baseBookingPreferences,
}: {
  context: CalendarContext;
  state: CalendarState;
  baseBookingPreferences?: BookingPreference[];
}): TimeSlotDetails => {
  const { calendarSelections, t, bookingsSettings } = context;
  const { serviceVariantsMap, servicesInView } = state;
  const service = servicesInView[0];
  const preSelectedPreferences = calendarSelections?.services?.[0];

  const getPreferenceValueFromDropdownIfOnlyOneOption = (
    preferenceKey: Preference,
  ): string | undefined => {
    const preference = baseBookingPreferences?.find(
      (pref) => pref.key === preferenceKey,
    );
    return preference?.options.length === 1
      ? preference.options[0].value
      : undefined;
  };

  const getLocationFromCalendarSelections = (): string | undefined => {
    const locationId = calendarSelections?.location;
    if (!locationId) {
      return;
    }
    return service?.locations?.find((l) => l.id === locationId)?.business?.name;
  };

  const getStaffMemberFromCalendarSelections = (): string | undefined => {
    const staffIds = preSelectedPreferences?.resources;
    if (staffIds?.length !== 1) {
      return;
    }
    const staffId = staffIds[0];
    if (staffId === DO_NOT_CARE_STAFF) {
      return t('services-preferences-modal.summary.select-any-staff');
    }

    const staffName = service?.staffMembers?.find(
      (staff) => staff.staffMemberId === staffId,
    )?.name;
    if (staffName) {
      return staffName;
    }
  };

  const locationLabel =
    getLocationFromCalendarSelections() ??
    getPreferenceValueFromDropdownIfOnlyOneOption(Preference.LOCATION);

  const staffMemberLabel =
    getStaffMemberFromCalendarSelections() ??
    getPreferenceValueFromDropdownIfOnlyOneOption(Preference.STAFF_MEMBER);

  const durationLabel = getPreferenceValueFromDropdownIfOnlyOneOption(
    Preference.DURATION,
  );

  return {
    locationLabel,
    staffMemberLabel:
      isCustomerCannotChooseStaff(bookingsSettings) &&
      !isStaffVariant(serviceVariantsMap[service.id!])
        ? undefined
        : staffMemberLabel,
    durationLabel,
    addOnsLabel: getAddOnsSummary({ context, state }),
  };
};

const getAddOnsSummary = ({
  context,
  state,
}: {
  context: CalendarContext;
  state: CalendarState;
}): string | undefined => {
  const { calendarSelections, t, languageSettings } = context;

  const selectedFromA1 = calendarSelections?.services?.[0]?.addOns;
  const selectedFromReschedule = state.rescheduleBookingDetails?.bookedAddOns;
  const selected = selectedFromA1 || selectedFromReschedule;

  if (!selected?.length) {
    return;
  } else if (selectedFromReschedule?.length) {
    return t('app.summary.addons-count', {
      count: selectedFromReschedule.length,
    });
  }

  let totalPrice = 0;
  for (const item of selectedFromA1!) {
    const quantity = item.quantity;
    if (item.price) {
      totalPrice += item.price * quantity;
    }
  }
  const addOnsCount = selectedFromA1?.length!;
  const label = t('app.summary.addons-count', { count: addOnsCount });
  if (totalPrice > 0) {
    const currency = getServiceCurrency(state.servicesInView[0]);
    if (!currency) {
      return label;
    }
    const formattedPrice = getFormattedCurrency({
      price: totalPrice,
      currency,
      locale: languageSettings.regionalSettingsLocale!,
    });
    return `${label} (${formattedPrice})`;
  }

  return label;
};
